import { determineParentDate, determineParentRollupStatus, findDefaultItem, generateListDataForRollupStatus, handleEmptyParentNodeData, parseRowData, processChildValues, processParentNodeData, resetCell, updateChildStatus, updateNodeData } from "../helperFunctions";
import { updateCellValue } from "../xTablesHelper";

interface Row {
  id: string;
  [key: string]: any;
}

interface Column {
  linkedColumns: any;
  field: any;
  listData: string; // JSON string
  defaultStatus?: string; // JSON string
  // other props...
}
const processLeafNodeFlat = (
  row: any,
  col: string,
  listData: any[],
  rows: any,
  processedRows: any,
  loggedInUser: any,
  defaultStatus: any
) => {
  if (!row) return;

  const backlogItem =
    defaultStatus ??
    listData.find(
      item => item.parent === 'Default Status' && item.rowId === 'None'
    );

  // If column missing or empty
  if (!(col in row) || !row[col]) {
    if (
      backlogItem &&
      row.primaryCol_es?.value &&
      row.primaryCol_es.value.trim() !== ''
    ) {
      const rowData = {
        rowId: backlogItem.rowId,
        value: backlogItem.value,
      };
      resetCell(row, col, null, loggedInUser, JSON.stringify(rowData));
    }
  }

  processedRows[row.id] = row;

  let rowData = parseRowData(row[col]);

  // If invalid or None → reset to backlog
  if (!rowData || rowData?.rowId === 'None') {
    if (
      backlogItem &&
      row.primaryCol_es?.value &&
      row.primaryCol_es.value.trim() !== ''
    ) {
      const newRowData = JSON.stringify({
        rowId: backlogItem.rowId,
        value: backlogItem.value,
      });
      resetCell(row, col, null, loggedInUser, newRowData);
    }
    return;
  }

  // rowId-based match
  if (rowData?.rowId) {
    const listDataItem = listData.find(
      item => item.rowId === rowData.rowId
    );

    if (listDataItem && listDataItem.value !== rowData.value) {
      updateNodeData(
        row,
        col,
        rowData,
        listDataItem.value,
        null,
        loggedInUser
      );
    } else if (!listDataItem) {
      resetCell(row, col, null, loggedInUser);
    }
  }
  // value-based match
  else {
    const listDataItem = listData.find(
      item => item.value === rowData
    );

    const newValue = listDataItem?.value ?? '';

    if (newValue !== rowData) {
      if (
        backlogItem &&
        row.primaryCol_es?.value &&
        row.primaryCol_es.value.trim() !== ''
      ) {
        const fallbackRowData = JSON.stringify({
          rowId: backlogItem.rowId,
          value: backlogItem.value,
        });
        resetCell(row, col, null, loggedInUser, fallbackRowData);
      } else {
        resetCell(row, col, null, loggedInUser);
      }
    }
  }

  processedRows[row.id] = row;
};

function getImmediateChildren(parentId: string, allRows: Row[]) {
  const parentDepth = parentId.split('.').length;

  return allRows.filter(row => {
    if (!row.l_id.startsWith(parentId + '.')) return false;

    const rowDepth = row.l_id.split('.').length;
    return rowDepth === parentDepth + 1;
  });
}
const updateStatusRecursivelyFlat = (
  parentRow: any,
  col: string,
  listData: any[],
  precedenceMapping: any,
  rows: any,
  processedRows: Record<string, any>,
  loggedInUser: any,
  defaultStatus: any,
  allrows: any
) => {
  if (!parentRow || processedRows[parentRow.id]) return;

  const childRows = getImmediateChildren(parentRow.l_id, allrows);

  // Leaf node → handle separately
  if (!childRows || childRows.length === 0) {
    processLeafNodeFlat(
      parentRow,
      col,
      listData,
      '',
      processedRows,
      loggedInUser,
      defaultStatus
    );
    return;
  }

  // 1️⃣ Recursively process children first
  childRows.forEach((child) => {
    if (!processedRows[child.id]) {
      updateStatusRecursivelyFlat(
        child,
        col,
        listData,
        precedenceMapping,
        rows,
        processedRows,
        loggedInUser,
        defaultStatus,
        allrows
      );
    }
  });

  // 2️⃣ Collect children statuses
  const statuses: any[] = [];

  childRows.forEach((child) => {
    const childRow = processedRows[child.id] || child;

    if (childRow && col in childRow) {
      updateChildStatus(
        childRow,
        col,
        listData,
        null,              // rowNodes not needed for flat
        statuses,
        processedRows,
        loggedInUser,
        defaultStatus
      );
    } else if (childRow?.primaryCol_es?.value?.trim()) {
      const rowData = {
        rowId: defaultStatus.rowId,
        value: defaultStatus.value,
      };
      resetCell(
        childRow,
        col,
        null,
        loggedInUser,
        JSON.stringify(rowData)
      );
      statuses.push(rowData);
    } else {
      statuses.push('');
    }
  });

  // 3️⃣ Roll up parent status
  const parentStatus =
    statuses.length > 0
      ? determineParentRollupStatus(statuses, precedenceMapping, listData)
      : '';

  parentRow[col] = parentStatus;
  parentRow[col + '_es'] = updateCellValue(
    parentRow,
    col + '_es',
    'value',
    parentStatus,
    loggedInUser
  );

  processedRows[parentRow.id] = parentRow;
};


export function getParentStatusRollupForFlatRows(
  parentNode,          // the parent row object to update         
  column: any,       // column definition object for the status column
  updatedChildRowData,// object to track updated rows
  loggedInUser,       // user info, can be null if not needed
  rowByIdMap,
  rows
) {
  // parse listData and precedenceMapping
  const { listData, precedenceMapping } = generateListDataForRollupStatus(JSON.parse(column.listData));

  // Parse default status or create fallback
  let defaultStatus = column?.defaultStatus ? JSON.parse(column.defaultStatus) : {
    value: 'Backlog',
    parent: 'Default Status',
    precedence: '0:0',
    rowId: 'None',
  };

  let defaultItem = findDefaultItem(listData, defaultStatus);
  if (!defaultItem) {
    listData.push(defaultStatus);
  } else {
    defaultStatus = defaultItem;
  }

  // Filter children rows of the parentRow from allRows using parentIdField
  // const childRows = allRows.filter(row => row[parentIdField] === parentRow.id);
  const childRows = getImmediateChildren(parentNode.l_id, rows);

  // Extract statuses from child rows
  const statuses: string[] = [];
  let col = column.field

  if (childRows?.length > 0) {
    updateStatusRecursivelyFlat(parentNode, col, listData, precedenceMapping, '', updatedChildRowData, loggedInUser, defaultStatus, rows);
  } else {
    processLeafNodeFlat(parentNode, col, listData, '', updatedChildRowData, loggedInUser, defaultStatus);
  }

}

interface ParentDateRollupForFlatRowsParams {
  parentRow: any;
  column: Column;
  rollupType?: string;
  updatedChildRowData: any;
  rows: Row[];
  loggedInUser: any;
}


export function getParentDateRollupForFlatRows({
  parentRow,
  column,
  rollupType,
  updatedChildRowData,
  rows,
  loggedInUser,
}: ParentDateRollupForFlatRowsParams) {
  const col = column.field;

  // 1️ Get immediate children (flat hierarchy)
  const childRows = getImmediateChildren(parentRow.l_id, rows);
  if (!childRows.length) return;

  // 2️ Collect child date values
  let dates = [];
  const emptyChildrenValue = processChildValues(childRows, updatedChildRowData, col, dates, true);

  // 3️ Apply rollup
  if (dates.length > 0) {
    const parentDate = determineParentDate(
      column,
      rollupType,
      dates,
      parentRow,
      emptyChildrenValue
    );

    processParentNodeData(
      column,
      col,
      parentRow,
      parentDate,
      loggedInUser
    );
  } else {
    handleEmptyParentNodeData(
      column,
      col,
      parentRow,
      loggedInUser
    );
  }
}

interface CleanupDependencyParams {
  rowsToDeleteIds: string[];
  dependencyMap:any;
  rowByIdMap: Record<string, Row>;
  predecessorColumn: Column;
}
const getRowById = (
  rowByIdMap: any,
  rowId: string
): Row | undefined => {
  for (const entry of rowByIdMap) {
    if (entry[0]===rowId) {
      return entry[1];
    }
  }
  return undefined;
};
const removeDeletedPredecessors = (
  value: string,
  rowsToDeleteIds: string[]
): string => {
  return value
    .split(',')
    .map(v => v.trim())
    .filter(v => {
      const baseRowId = v.split('+')[0]; // rowId from rowId+2d
      return !rowsToDeleteIds.includes(baseRowId);
    })
    .join(',');
};


export const cleanupPredecessorSuccessorLinksForCardView = ({
  rowsToDeleteIds,
  dependencyMap,
  rowByIdMap,
  predecessorColumn,
}: CleanupDependencyParams) => {

  if (!predecessorColumn) {
    return { updatedRows: [], undoRows: [] };
  }
  const updatedRows: Row[] = [];
  const undoRowMap: Record<string, Row> = {};

  const impactedSuccessorIds = new Set<string>();
  const impactedPredecessorIds = new Set<string>();

  /* -------- Collect impacted rows from dependencyMap -------- */
  rowsToDeleteIds.forEach((deletedRowId) => {
    const dependency = dependencyMap[deletedRowId];
    if (!dependency) return;

    dependency.successorIds?.forEach(id => impactedSuccessorIds.add(id));
    dependency.predecessorIds?.forEach(id => impactedPredecessorIds.add(id));
  });

  /* -------- Remove deleted rows from successorNodes -------- */
  impactedSuccessorIds.forEach((rowId) => {
   
    const row = getRowById(rowByIdMap, rowId);
    if (!row?.predecessorNodes) return;

    const undoRow = structuredClone(row);

    const filteredpredecessors = row.predecessorNodes
      .split(',')
      .filter(id => !rowsToDeleteIds.includes(id))
      .join(',');

    if (filteredpredecessors !== row.predecessorNodes) {
      row.predecessorNodes = filteredpredecessors;
      const field = predecessorColumn.field;

      if (row[field]) {
        const cleanedValue = removeDeletedPredecessors(
          row[field],
          rowsToDeleteIds
        );

        if (cleanedValue !== row[field]) {
          row[field] = cleanedValue;
        }
      }
      updatedRows.push(row);
      undoRowMap[row.id] = undoRow;
    }
  });

  /* -------- Remove deleted rows from predecessorNodes -------- */
  impactedPredecessorIds.forEach((rowId) => {
    const row = getRowById(rowByIdMap, rowId);
    if (!row?.successorNodes) return;

    const undoRow = structuredClone(row);

    const filteredsuccessors = row.successorNodes
      .split(',')
      .filter(id => !rowsToDeleteIds.includes(id))
      .join(',');

    if (filteredsuccessors !== row.successorNodes) {
      row.successorNodes = filteredsuccessors;
      updatedRows.push(row);
      undoRowMap[row.id] = undoRow;
    }
  });

  return {
    updatedRows,
    undoRows: Object.values(undoRowMap),
  };
};
